import { Link } from "@tanstack/react-router";
import { ChevronRight, ChevronsRight } from "lucide-react";
import React from "react";

import { cn } from "~/lib/cn";

interface BreadcrumbProps {
  className?: string;
  items: { label: string; href?: string }[];
  variant?: "chevron" | "chevrons";
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
  isBold?: boolean;
  isHeading?: boolean;
  useGrayLinks?: boolean;
  theme?: "light" | "dark";
}

interface BreadcrumbItem {
  label: string;
  href?: string;
}

/** Trail of links showing where the current page sits. */
export const Breadcrumb = (props: BreadcrumbProps) => {
  const {
    variant = "chevron",
    size = "sm",
    isBold = true,
    isHeading = false,
    useGrayLinks = true,
    theme = "light",
  } = props;

  const isDark = theme === "dark";

  const Separator = variant === "chevrons" ? ChevronsRight : ChevronRight;

  const textSizeClass = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
    "3xl": "text-3xl",
  }[size];

  const fontClass = cn(
    textSizeClass,
    isBold && "font-medium",
    isHeading && "font-heading",
  );

  const separatorColorClass = isDark
    ? "text-purple-300/50"
    : useGrayLinks
      ? "text-gray-400"
      : "text-black";

  const currentPageClass = isDark ? "text-white" : "text-black";

  const linkClass = isDark
    ? "text-purple-200/80 hover:text-white"
    : useGrayLinks
      ? "text-gray-800 hover:text-gray-600"
      : "text-black hover:text-black/80";

  return (
    <nav className={cn("mb-4 flex", props.className)} aria-label="Breadcrumb">
      <ol className="inline-flex items-center gap-2">
        {props.items.map((item: BreadcrumbItem, index: number) => {
          const isLast = index === props.items.length - 1;
          const hasHref = item.href !== undefined;
          return (
            <li
              key={item.href ?? item.label}
              className="inline-flex items-center gap-2 leading-none"
            >
              {index > 0 && (
                <Separator
                  className={cn(
                    "mt-0.5",
                    separatorColorClass,
                    size === "sm" && "h-4 w-4",
                    size === "md" && "h-5 w-5",
                    (size === "lg" || size === "xl") && "h-6 w-6",
                    (size === "2xl" || size === "3xl") && "h-7 w-7",
                  )}
                />
              )}
              {isLast || !hasHref ? (
                <p
                  className={cn(
                    "h-full leading-none",
                    currentPageClass,
                    fontClass,
                  )}
                >
                  {item.label}
                </p>
              ) : (
                <Link
                  to={item.href}
                  className={cn("h-full leading-none", linkClass, fontClass)}
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
